import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { usePropertyFilter } from '@/utils/usePropertyFilter';
import * as Sentry from '@sentry/react';
import '@/styles/SiteVisitsInputPage.scss';

const SiteVisitsInputPage = () => {
    const { apiFetch } = useAuth();
    const { accessiblePropertyIDs, isUnrestricted, isLoading: propertiesLoading } = usePropertyFilter();

    const todayStr = new Date().toISOString().split('T')[0];

    const [allProperties, setAllProperties] = useState([]);
    const [propertyID, setPropertyID] = useState('');
    const [visitDate, setVisitDate] = useState(todayStr);
    const [loading, setLoading] = useState(false);

    // Fetch all properties then filter based on the user's access restrictions
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const active = data.data.filter(p => p.active !== false);
                    setAllProperties(active);
                }
            } catch (e) {
                Sentry.captureException(e);
                toast.error('Failed to load properties.');
            }
        };
        fetchProperties();
    }, [apiFetch]);

    // Once both property list and access restrictions are ready, resolve the visible list
    const visibleProperties = isUnrestricted
        ? allProperties
        : allProperties.filter(p => accessiblePropertyIDs?.includes(p.propertyID));

    // Set the initial selected property once the visible list is resolved
    useEffect(() => {
        if (visibleProperties.length > 0 && !propertyID) {
            setPropertyID(visibleProperties[0].propertyID);
        }
    }, [visibleProperties, propertyID]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!visitDate || !propertyID) {
            toast.error('Please fill in all fields before submitting.');
            return;
        }

        setLoading(true);
        try {
            const response = await apiFetch('/api/Ops/SiteVisit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    PropertyID: parseInt(propertyID),
                    DateVisited: visitDate, // "yyyy-MM-dd" — avoids UTC offset shift
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                toast.success('Site visit recorded successfully!');
                setVisitDate(todayStr);
                if (visibleProperties.length > 0) setPropertyID(visibleProperties[0].propertyID);
            } else {
                toast.error(data.message || 'Failed to record site visit.');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('An error occurred while submitting the site visit.');
        } finally {
            setLoading(false);
        }
    };

    const isResolvingProperties = propertiesLoading || allProperties.length === 0;

    return (
        <div className="site-visits-input-page">
            <h1>Log Site Visit</h1>
            <form className="site-visits-form" onSubmit={handleSubmit}>

                <div className="form-group">
                    <label htmlFor="visitDate">Date of Visit</label>
                    <input
                        type="date"
                        id="visitDate"
                        value={visitDate}
                        max={todayStr}
                        onChange={e => setVisitDate(e.target.value)}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="property">Property</label>
                    <select
                        id="property"
                        value={propertyID}
                        onChange={e => setPropertyID(e.target.value)}
                        required
                        disabled={isResolvingProperties}
                    >
                        {isResolvingProperties ? (
                            <option value="">Loading properties...</option>
                        ) : visibleProperties.length > 0 ? (
                            visibleProperties.map(p => (
                                <option key={p.propertyID} value={p.propertyID}>
                                    {p.propertyName}
                                </option>
                            ))
                        ) : (
                            <option value="">No properties assigned</option>
                        )}
                    </select>
                </div>

                <div className="form-actions">
                    <button type="submit" className="btn-submit" disabled={loading || isResolvingProperties}>
                        {loading ? 'Submitting...' : 'Submit Visit'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default SiteVisitsInputPage;